import { AlertTriangle, ArrowRightLeft, CalendarOff, CheckCircle2, CircleDashed, Clock3, RefreshCcw, ShieldCheck } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

import { attendanceStatusTone, formatAttendanceStatusLabel, formatShortDate } from './payrollAttendanceUtils';

const MATRIX_LEGEND = [
  { key: 'presente', label: 'Presente', icon: CheckCircle2 },
  { key: 'falta', label: 'Falta', icon: AlertTriangle },
  { key: 'falta_justificada', label: 'Falta justificada', icon: Clock3 },
  { key: 'outside_employment_window', label: 'Fora do vínculo', icon: CalendarOff },
  { key: 'holiday', label: 'Feriado', icon: CalendarOff },
  { key: 'holiday_worked', label: 'Feriado trabalhado', icon: ShieldCheck },
  { key: 'sunday', label: 'Domingo', icon: CircleDashed },
  { key: 'folga', label: 'Folga', icon: CalendarOff },
  { key: 'troca_de_folga', label: 'Troca de folga', icon: RefreshCcw },
  { key: 'folga_compensatoria', label: 'Folga compensatória', icon: ShieldCheck },
  { key: 'pending', label: 'Pendência', icon: CircleDashed },
];

export const PayrollAttendanceMatrix = ({
  days,
  employees,
  selectedAttendanceCell,
  onSelectCell,
  employeeSummaries,
  employeeTransfers,
  getCellMeta,
}) => {
  return (
    <div className="overflow-hidden rounded-md border border-slate-200 bg-white shadow-sm" data-testid="payroll-attendance-matrix">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-200 px-3 py-3">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-500">Matriz mensal</p>
          <h3 className="text-lg font-extrabold tracking-tight text-slate-950" style={{ fontFamily: 'Manrope, sans-serif' }} data-testid="payroll-attendance-matrix-title">
            Visão densa por colaborador e dia
          </h3>
        </div>
        <div className="flex flex-wrap items-center gap-1.5 text-[11px]" data-testid="payroll-attendance-matrix-legend">
          {MATRIX_LEGEND.map((item) => {
            const Icon = item.icon;
            return (
              <Badge key={item.key} variant="outline" className="rounded-sm bg-white px-2 py-1">
                <Icon className="mr-1 h-3.5 w-3.5" />
                {item.label}
              </Badge>
            );
          })}
          <Badge variant="outline" className="rounded-sm bg-indigo-50 text-indigo-700">
            <ArrowRightLeft className="mr-1 h-3.5 w-3.5" />
            Transferência
          </Badge>
        </div>
      </div>

      <div className="overflow-x-auto overflow-y-hidden">
        <table className="w-full min-w-[1180px] border-collapse text-xs" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
          <thead>
            <tr className="bg-slate-50 text-[11px] uppercase tracking-[0.16em] text-slate-500">
              <th className="sticky left-0 z-20 min-w-[260px] border-b border-r border-slate-200 bg-slate-50 px-3 py-2 text-left">Colaborador</th>
              {days.map((day) => (
                <th key={day.dateKey} className={cn('min-w-[42px] border-b border-slate-200 px-1 py-2 text-center', day.isHoliday ? 'bg-rose-50' : (day.isSunday ? 'bg-slate-100' : ''))} data-testid={`payroll-attendance-matrix-day-${day.dateKey}`}>
                  <div className={cn('font-semibold', day.isHoliday ? 'text-rose-700' : 'text-slate-800')}>{String(day.day).padStart(2, '0')}</div>
                  <div className="mt-0.5 text-[10px] text-slate-500">{day.weekday}</div>
                  {day.isHoliday && (
                    <div className="mt-1 text-[9px] font-semibold leading-tight text-rose-700" data-testid={`payroll-attendance-holiday-name-${day.dateKey}`}>{day.holidayName}</div>
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {employees.length === 0 ? (
              <tr>
                <td colSpan={days.length + 1} className="px-4 py-12 text-center text-sm text-slate-500" data-testid="payroll-attendance-matrix-empty-state">
                  Sem colaboradores visíveis para os filtros actuais.
                </td>
              </tr>
            ) : employees.map((employee) => {
              const summary = employeeSummaries.get(employee.employee_number) || {};
              const transfers = employeeTransfers.get(employee.employee_number) || [];
              return (
                <tr key={employee.id || employee.employee_number} className="align-top" data-testid={`payroll-attendance-row-${employee.employee_number}`}>
                  <td className="sticky left-0 z-10 border-b border-r border-slate-200 bg-white px-3 py-2">
                    <div className="space-y-1.5">
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <p className="text-sm font-semibold text-slate-950" data-testid={`payroll-attendance-row-name-${employee.employee_number}`}>{employee.full_name || employee.name}</p>
                          <p className="text-[11px] text-slate-500" data-testid={`payroll-attendance-row-meta-${employee.employee_number}`}>
                            {employee.employee_number} · {summary.store_location || employee.store_location || 'Sem loja'}
                          </p>
                        </div>
                        <div className="text-right text-[11px] text-slate-500">
                          <p data-testid={`payroll-attendance-row-hours-${employee.employee_number}`}>{summary.total_hours || 0}h</p>
                          <p data-testid={`payroll-attendance-row-issues-${employee.employee_number}`}>{summary.multi_store_days || 0} multi-loja · {summary.issue_count || 0} issue(s)</p>
                        </div>
                      </div>

                      <div className="flex flex-wrap gap-1" data-testid={`payroll-attendance-row-transfer-strip-${employee.employee_number}`}>
                        {transfers.slice(0, 3).map((transfer) => (
                          <Badge key={transfer.id} variant="outline" className="rounded-sm border-indigo-200 bg-indigo-50 text-[10px] text-indigo-700">
                            <ArrowRightLeft className="mr-1 h-3 w-3" />
                            {transfer.store_location} · {formatShortDate(transfer.start_date)}–{formatShortDate(transfer.end_date || transfer.start_date)}
                          </Badge>
                        ))}
                        {transfers.length === 0 && <span className="text-[10px] text-slate-400">Sem transferência no período</span>}
                      </div>
                    </div>
                  </td>

                  {days.map((day) => {
                    const cell = getCellMeta(employee, day.dateKey);
                    const isSelected = selectedAttendanceCell?.employee_number === employee.employee_number && selectedAttendanceCell?.date === day.dateKey;
                    return (
                      <td key={`${employee.employee_number}-${day.dateKey}`} className="border-b border-slate-200 p-1">
                        <button
                          type="button"
                          onClick={() => onSelectCell({ employee_id: employee.id, employee_number: employee.employee_number, date: day.dateKey })}
                          className={cn(
                            'relative flex h-10 w-full min-w-[38px] flex-col items-center justify-center rounded-sm border text-[10px] font-semibold transition-colors',
                            attendanceStatusTone[cell.status],
                            isSelected && 'ring-2 ring-blue-600 ring-offset-1',
                          )}
                          data-testid={`payroll-attendance-cell-${employee.employee_number}-${day.dateKey}`}
                          title={`${employee.full_name || employee.name} · ${day.dateKey} · ${cell.label}${cell.calendarLabel ? ` · ${cell.calendarLabel}` : ''}`}
                        >
                          {cell.isTransfer && <span className="absolute inset-x-0 top-0 h-1 rounded-t-sm bg-indigo-500" aria-hidden="true" />}
                          <span>{cell.shortLabel}</span>
                          <span className="mt-0.5 text-[9px] font-normal">{cell.metaLabel || day.day}</span>
                        </button>
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};